import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, customersSignal, searchCustomersForAutocomplete } from './assets';

export const memosSignal = signal<any[]>([]);
export const allAssetsSignal = signal<any[]>([]);
export const allDepartmentsSignal = signal<any[]>([]);
export const selectedMemoIdSignal = signal<string | null>(null);

export async function refreshMemos() {
	try {
		const res = await fetch('/api/v1/internal-memos', { headers: getAuthHeaders() });
		if (res.ok) {
			memosSignal.value = await res.json();
		} else {
			memosSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch internal memos', e);
	}
}

export async function initBillingSignals() {
	try {
		const h = getAuthHeaders();
		const resAssets = await fetch('/api/v1/assets', { headers: h });
		if (resAssets.ok) {
			allAssetsSignal.value = await resAssets.json();
		}

		const resDepts = await fetch('/api/v1/departments', { headers: h });
		if (resDepts.ok) {
			allDepartmentsSignal.value = await resDepts.json();
		}

		await refreshMemos();
	} catch (e) {
		console.error('Failed to initialize billing signals', e);
	}
}

export function renderBillingPage(): string {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const memos = memosSignal.value || [];

	return `
		<s-page heading="Internal Memos" subtitle="Record cost allocations, post expense memos, and compile invoice PDF documents." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction">
					<s-button variant="primary" id="btn-open-create-memo">Create Memo</s-button>
				</div>
			` : ''}

			<!-- Memos List Section -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${memos.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No internal memos logged in system.</s-text>
						</div>
					` : `
						<opm-data-table
							id="memos-table"
							searchable="true"
							paginated="true"
							page-size="10"
							empty-message="No memos found."
							search-placeholder="Search memos..."
						></opm-data-table>
					`}
				</s-box>
			</s-section>

			<!-- Create Memo Modal -->
			<s-modal id="create-memo-modal" heading="Record Financial Internal Memo">
				<s-form id="form-create-memo">
					<s-stack gap="base">
						<div style="display:grid; grid-template-columns:1fr 1fr; gap:1rem;">
							<div style="display:flex; flex-direction:column; gap:0.25rem;">
								<label style="font-size:0.875rem; font-weight:500;">Customer Organization</label>
								<opm-autocomplete
									id="create-memo-customer-select"
									label="Customer Organization"
									placeholder="Search customers..."
								></opm-autocomplete>
								<input type="hidden" name="customerId" id="create-memo-customer-id" />
							</div>

							<div style="display:flex; flex-direction:column; gap:0.25rem;">
								<label style="font-size:0.875rem; font-weight:500;">Memo Type</label>
								<select name="memoType" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;" required>
									<option value="revenue">Revenue (Income Allocation)</option>
									<option value="expense">Expense (Direct Cost/Fee)</option>
								</select>
							</div>
						</div>

						<div style="display:grid; grid-template-columns:1fr 1fr; gap:1rem;">
							<div style="display:flex; flex-direction:column; gap:0.25rem;">
								<label style="font-size:0.875rem; font-weight:500;">Target Context Allocation</label>
								<select name="linkType" id="create-memo-link-type" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<option value="none">Customer-wide (No link)</option>
									<option value="department">Department-wide</option>
									<option value="asset">Specific Machine (Asset)</option>
								</select>
							</div>
							
							<div id="create-memo-dept-wrapper" style="display:none; flex-direction:column; gap:0.25rem;">
								<label style="font-size:0.875rem; font-weight:500;">Select Target Department</label>
								<select name="departmentId" id="create-memo-department-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<!-- Populated dynamically -->
								</select>
							</div>

							<div id="create-memo-asset-wrapper" style="display:none; flex-direction:column; gap:0.25rem;">
								<label style="font-size:0.875rem; font-weight:500;">Select Target Machine (Serial)</label>
								<select name="assetId" id="create-memo-asset-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<!-- Populated dynamically -->
								</select>
							</div>
						</div>

						<s-grid gridTemplateColumns="1fr 1fr" gap="small">
							<s-text-field label="Period Start" name="periodStart" type="date" required></s-text-field>
							<s-text-field label="Period End" name="periodEnd" type="date" required></s-text-field>
						</s-grid>

						<s-heading element="h3">Multi-Line Items List</s-heading>
						<div style="border:1px solid var(--p-color-border-subdued); border-radius:6px; padding:0.75rem; background:var(--p-color-bg-surface-secondary);">
							<div style="display:grid; grid-template-columns: 2fr 3fr 1fr 1.5fr 1.5fr auto; gap:0.5rem; font-weight:600; font-size:0.75rem; text-transform:uppercase; color:var(--p-color-text-subdued); border-bottom:1px solid var(--p-color-border-subdued); padding-bottom:0.25rem; margin-bottom:0.5rem;">
								<span>Category</span>
								<span>Description</span>
								<span>Qty</span>
								<span>Unit Price</span>
								<span>Total</span>
								<span></span>
							</div>
							<div id="memo-items-container" style="display:flex; flex-direction:column; gap:0.5rem;">
								<!-- Dynamic items rows -->
							</div>
							<div style="margin-top:1rem; display:flex; justify-content:space-between; align-items:center;">
								<s-button id="btn-add-memo-item-line" variant="secondary" size="slim">Add Line Item</s-button>
								<s-text><strong>Grand Total:</strong> NRs. <span id="create-memo-grand-total" style="font-weight:600; font-size:1.05rem;">0.00</span></s-text>
							</div>
						</div>

						<div id="create-memo-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem; font-weight:500;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-memo">Submit Memo</s-button>
							<s-button commandFor="create-memo-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Nullify Link Confirmation Modal -->
			<s-modal id="nullify-link-modal" heading="Sever Machine Link Allocation?">
				<s-stack gap="base">
					<s-text tone="critical">Warning: Severing the machine reference preserves the transactional memo amount under the Customer account context but clears the machine specific allocation index permanently.</s-text>
					<div id="nullify-link-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-nullify-link">Sever Allocation</s-button>
						<s-button commandFor="nullify-link-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupBillingPageListeners() {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const memos = memosSignal.value || [];
	const customers = customersSignal.value || [];
	const assets = allAssetsSignal.value || [];
	const departments = allDepartmentsSignal.value || [];

	const customerMap = new Map(customers.map(c => [c.id, c.name]));
	const assetMap = new Map(assets.map(a => [a.id, a.serialNumber]));
	const deptMap = new Map(departments.map(d => [d.id, d.name]));

	// Dynamic Row ID generator
	let itemRowCounter = 0;

	const addRowLine = () => {
		const container = document.getElementById('memo-items-container');
		if (!container) return;

		const rowId = `item-row-${itemRowCounter++}`;
		const rowHtml = `
			<div class="memo-item-row" data-row-id="${rowId}" style="display:grid; grid-template-columns: 2fr 3fr 1fr 1.5fr 1.5fr auto; gap:0.5rem; align-items:center;">
				<select class="item-category" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:36px;">
					<option value="rental_charge">Rental Charge</option>
					<option value="paper_charge">Paper Charge</option>
					<option value="consumable">Consumable</option>
					<option value="service_retainer">Service Retainer</option>
					<option value="setup_cost">Setup Cost</option>
					<option value="overage_charge">Overage Charge</option>
					<option value="other">Other</option>
				</select>
				<input type="text" placeholder="Item description..." class="item-name" required style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; font-size:0.875rem; background:var(--p-color-bg-surface); color:var(--p-color-text);" />
				<input type="number" placeholder="Qty" value="1" class="item-qty" min="1" required style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; width:50px; font-size:0.875rem; background:var(--p-color-bg-surface); color:var(--p-color-text);" />
				<input type="number" step="0.01" placeholder="Price" value="0.00" class="item-price" min="0" required style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; font-size:0.875rem; background:var(--p-color-bg-surface); color:var(--p-color-text);" />
				<span style="font-size:0.9rem; font-weight:500;">NRs. <span class="item-total-amount">0.00</span></span>
				<button class="btn-remove-memo-row" style="background:none; border:none; color:var(--p-color-text-critical); cursor:pointer; font-size:1.1rem; padding:0 0.5rem;" title="Remove row">✕</button>
			</div>
		`;

		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = rowHtml;
		const rowNode = tempDiv.firstElementChild;
		if (rowNode) {
			container.appendChild(rowNode);

			// Bind remove listener
			rowNode.querySelector('.btn-remove-memo-row')?.addEventListener('click', () => {
				rowNode.remove();
				recalculateGrandTotal();
			});
		}

		recalculateGrandTotal();
	};

	const recalculateGrandTotal = () => {
		const rows = document.querySelectorAll('.memo-item-row');
		let grandTotal = 0;

		rows.forEach((row: any) => {
			const qtyInput = row.querySelector('.item-qty') as HTMLInputElement;
			const priceInput = row.querySelector('.item-price') as HTMLInputElement;
			const totalSpan = row.querySelector('.item-total-amount') as HTMLElement;

			if (qtyInput && priceInput && totalSpan) {
				const qty = Number(qtyInput.value || 0);
				const price = Number(priceInput.value || 0);
				const total = qty * price;
				totalSpan.textContent = total.toFixed(2);
				grandTotal += total;
			}
		});

		const totalSpan = document.getElementById('create-memo-grand-total');
		if (totalSpan) {
			totalSpan.textContent = grandTotal.toFixed(2);
		}
	};

	// 1. Open Create Modal Listener
	const openCreateBtn = document.getElementById('btn-open-create-memo');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-memo-modal') as any;
			const errDiv = document.getElementById('create-memo-error');
			if (errDiv) errDiv.style.display = 'none';

			// Clear items rows and add one empty row
			const container = document.getElementById('memo-items-container');
			if (container) container.innerHTML = '';
			itemRowCounter = 0;
			addRowLine();

			// Pre-select Customer and trigger its dropdown dependencies
			updateCreateFormDropdowns();

			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 2. Add Item Line button
	const addLineBtn = document.getElementById('btn-add-memo-item-line');
	if (addLineBtn) {
		addLineBtn.addEventListener('click', (e) => {
			e.preventDefault();
			addRowLine();
		});
	}

	// Bind input events on items container for total calculations
	const itemsContainer = document.getElementById('memo-items-container');
	if (itemsContainer) {
		itemsContainer.addEventListener('input', () => {
			recalculateGrandTotal();
		});
	}

	// 3. Link Type selector toggles
	const linkTypeSelect = document.getElementById('create-memo-link-type') as HTMLSelectElement;
	if (linkTypeSelect) {
		linkTypeSelect.addEventListener('change', () => {
			const val = linkTypeSelect.value;
			const deptWrapper = document.getElementById('create-memo-dept-wrapper');
			const assetWrapper = document.getElementById('create-memo-asset-wrapper');

			if (deptWrapper) deptWrapper.style.display = val === 'department' ? 'flex' : 'none';
			if (assetWrapper) assetWrapper.style.display = val === 'asset' ? 'flex' : 'none';
		});
	}

	// 4. Customer selection change listener (updates dept and asset options)
	const customerSelect = document.getElementById('create-memo-customer-select') as any;
	if (customerSelect) {
		customerSelect.addEventListener('select', () => {
			updateCreateFormDropdowns();
		});
	}

	const updateCreateFormDropdowns = () => {
		const custSelect = document.getElementById('create-memo-customer-select') as any;
		if (!custSelect) return;
		const customerId = custSelect.selectedId || '';

		// Filter assets
		const filteredAssets = assets.filter(a => a.customerId === customerId && a.status === 'active');
		const assetSelect = document.getElementById('create-memo-asset-select') as HTMLSelectElement;
		if (assetSelect) {
			assetSelect.innerHTML = filteredAssets.map(a => `<option value="${a.id}">${a.serialNumber}</option>`).join('');
		}

		// Filter departments
		const filteredDepts = departments.filter(d => d.customerId === customerId);
		const deptSelect = document.getElementById('create-memo-department-select') as HTMLSelectElement;
		if (deptSelect) {
			deptSelect.innerHTML = filteredDepts.map(d => `<option value="${d.id}">${d.name}</option>`).join('');
		}
	};

	// 5. Submit Form
	const submitBtn = document.getElementById('btn-submit-create-memo');
	if (submitBtn) {
		submitBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-memo') as HTMLFormElement;
			if (!form) return;

			const errDiv = document.getElementById('create-memo-error');
			if (errDiv) errDiv.style.display = 'none';

			const customerId = (form.querySelector('[name="customerId"]') as HTMLSelectElement)?.value || '';
			const memoType = (form.querySelector('[name="memoType"]') as HTMLSelectElement)?.value || 'revenue';
			const linkType = (form.querySelector('[name="linkType"]') as HTMLSelectElement)?.value || 'none';
			const periodStart = (form.querySelector('[name="periodStart"]') as any)?.value || '';
			const periodEnd = (form.querySelector('[name="periodEnd"]') as any)?.value || '';

			let departmentId: string | null = null;
			let assetId: string | null = null;

			if (linkType === 'department') {
				departmentId = (form.querySelector('[name="departmentId"]') as HTMLSelectElement)?.value || null;
			} else if (linkType === 'asset') {
				assetId = (form.querySelector('[name="assetId"]') as HTMLSelectElement)?.value || null;
			}

			// Parse multi-line items
			const rows = form.querySelectorAll('.memo-item-row');
			const items: any[] = [];
			let invalidInputs = false;

			rows.forEach((row: any) => {
				const cat = (row.querySelector('.item-category') as HTMLSelectElement)?.value || '';
				const name = (row.querySelector('.item-name') as HTMLInputElement)?.value || '';
				const qty = Number((row.querySelector('.item-qty') as HTMLInputElement)?.value || 0);
				const price = Number((row.querySelector('.item-price') as HTMLInputElement)?.value || 0);
				const amount = qty * price;

				if (!name || qty <= 0 || price < 0) {
					invalidInputs = true;
				}

				items.push({
					category: cat,
					itemName: name,
					quantity: qty,
					unitPrice: price,
					amount: amount
				});
			});

			if (!periodStart || !periodEnd) {
				if (errDiv) {
					errDiv.textContent = 'Billing Period Start and End dates are required.';
					errDiv.style.display = 'block';
				}
				return;
			}

			if (periodEnd < periodStart) {
				if (errDiv) {
					errDiv.textContent = 'Billing Period End Date cannot be before Start Date.';
					errDiv.style.display = 'block';
				}
				return;
			}

			if (items.length === 0) {
				if (errDiv) {
					errDiv.textContent = 'At least one line item must be added.';
					errDiv.style.display = 'block';
				}
				return;
			}

			if (invalidInputs) {
				if (errDiv) {
					errDiv.textContent = 'All items require descriptions, positive quantities, and non-negative unit prices.';
					errDiv.style.display = 'block';
				}
				return;
			}

			try {
				const res = await fetch('/api/v1/internal-memos', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						customerId,
						memoType,
						departmentId,
						assetId,
						periodStart,
						periodEnd,
						items
					})
				});

				if (res.ok) {
					const modal = document.getElementById('create-memo-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshMemos();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create internal memo.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 6. Configure OpmDataTable
	const table = document.getElementById('memos-table') as any;
	if (table) {
		table.columns = [
			{ key: 'memoNumber', header: 'Memo Number', sortable: true, sortType: 'string', render: (row: any) => `<strong>${row.memoNumber}</strong>` },
			{
				key: 'periodStart',
				header: 'Billing Period',
				sortable: true,
				sortType: 'string',
				render: (row: any) => `<span style="font-size:0.85rem;">${row.periodStart} &rarr; ${row.periodEnd}</span>`
			},
			{ key: 'customerId', header: 'Customer', sortable: true, sortType: 'string', render: (row: any) => customerMap.get(row.customerId) || 'Unknown' },
			{
				key: 'targetEntity',
				header: 'Target Allocation',
				render: (row: any) => {
					if (row.assetId) {
						const serial = assetMap.get(row.assetId) || 'Deleted Machine';
						return `<s-badge tone="info">Machine: ${serial}</s-badge>`;
					}
					if (row.departmentId) {
						const deptName = deptMap.get(row.departmentId) || 'Deleted Dept';
						return `<s-badge tone="attention">Dept: ${deptName}</s-badge>`;
					}
					return `<s-text tone="subdued">Customer-wide</s-text>`;
				}
			},
			{
				key: 'memoType',
				header: 'Type',
				sortable: true,
				sortType: 'string',
				render: (row: any) => {
					const tone = row.memoType === 'revenue' ? 'success' : 'attention';
					return `<s-badge tone="${tone}">${row.memoType.toUpperCase()}</s-badge>`;
				}
			},
			{
				key: 'amount',
				header: 'Grand Total',
				render: (row: any) => {
					const total = (row.items || []).reduce((acc: number, item: any) => acc + (item.amount || 0), 0);
					return `<strong>NRs. ${total.toFixed(2)}</strong>`;
				}
			},
			{
				key: 'status',
				header: 'Status',
				render: () => `<s-badge tone="success">POSTED</s-badge>`
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => {
					const hasMachineLink = !!row.assetId;
					return `
						<div style="display: flex; gap: 0.5rem; align-items: center;">
							<s-button data-action="download-pdf" data-id="${row.id}" size="slim">PDF</s-button>
							${(!isReadOnly && hasMachineLink) ? `
								<s-button data-action="nullify" data-id="${row.id}" tone="critical" size="slim">Nullify Link</s-button>
							` : ''}
						</div>
					`;
				}
			}
		];
		table.items = memos;

		table.addEventListener('row-action', async (e: CustomEvent) => {
			const { action, id, item } = e.detail;
			selectedMemoIdSignal.value = id;

			if (action === 'download-pdf') {
				await downloadPDF(id, item.memoNumber);
			} else if (action === 'nullify') {
				const errDiv = document.getElementById('nullify-link-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('nullify-link-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			}
		});
	}

	// 7. Confirm Nullification action
	const confirmNullifyBtn = document.getElementById('btn-confirm-nullify-link');
	if (confirmNullifyBtn) {
		confirmNullifyBtn.addEventListener('click', async () => {
			const id = selectedMemoIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('nullify-link-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/internal-memos/${id}/nullify`, {
					method: 'POST',
					headers: getAuthHeaders()
				});

				if (res.ok) {
					const modal = document.getElementById('nullify-link-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshMemos();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to sever machine link.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

async function downloadPDF(memoId: string, memoNumber: string) {
	try {
		const res = await fetch(`/api/v1/internal-memos/${memoId}/pdf`, { headers: getAuthHeaders() });
		if (res.ok) {
			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${memoNumber}.pdf`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} else {
			alert('Failed to generate PDF document.');
		}
	} catch (e) {
		console.error('Failed to download invoice PDF', e);
	}
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
	const customerAutocomplete = document.getElementById('create-memo-customer-select');
	if (customerAutocomplete) {
		(customerAutocomplete as any).loadItems = searchCustomersForAutocomplete;
		customerAutocomplete.addEventListener('select', (e: any) => {
			const customerId = e.detail.id;
			const hidden = document.getElementById('create-memo-customer-id') as HTMLInputElement | null;
			if (hidden) hidden.value = customerId || '';
		});
	}
